import * as React from "react"
import { X } from "@/components/icons"

import { IconButton, type IconButtonProps } from "@/components/ui/icon-button"
import { TONE_TINT, TONE_SOLID, TONE_DOT, type Tone } from "./tone";
import { cn } from "@/lib/utils"

export type BadgeVariant = Tone | "outline" | "quiet"
export type BadgeSize = "sm" | "default" | "lg"
export type BadgeWeight = "normal" | "medium"

const VARIANT_TONE: Record<BadgeVariant, Tone> = {
  brand: "brand",
  neutral: "neutral",
  info: "info",
  azure: "azure",
  violet: "violet",
  success: "success",
  warning: "warning",
  destructive: "destructive",
  outline: "neutral",
  quiet: "neutral",
}

const badgeSizes: Record<BadgeSize, string> = {
  sm: "gap-0.5 px-1.5 py-0 text-overline tracking-normal",
  default: "gap-1 px-2 py-0.5 text-xs",
  lg: "gap-1.5 px-3 py-1 text-sm",
};

const badgeWeights: Record<BadgeWeight, string> = {
  normal: "font-normal",
  medium: "font-medium",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  tone?: Tone
  size?: BadgeSize
  weight?: BadgeWeight
  numeric?: boolean
  solid?: boolean
  dot?: boolean
}

function Badge({
  variant,
  tone,
  size = "default",
  weight,
  numeric = false,
  solid = false,
  dot = false,
  className,
  children,
  ...props
}: BadgeProps) {
  const resolved: BadgeVariant = tone ?? variant ?? "neutral"
  const resolvedTone = VARIANT_TONE[resolved]
  const isStructural = resolved === "outline" || resolved === "quiet"
  return (
    <span
      data-slot="badge"
      data-tone={resolvedTone}
      {...props}
      className={cn(
        "inline-flex items-center rounded-full border border-transparent transition-colors duration-fast outline-none focus-visible:ring-1 focus-visible:ring-ring",
        badgeSizes[size],
        badgeWeights[weight ?? (resolved === "outline" ? "normal" : "medium")],
        numeric && "tabular-nums",
        !isStructural && (solid ? TONE_SOLID[resolvedTone] : TONE_TINT[resolvedTone]),
        resolved === "outline" && "border-border bg-transparent text-foreground",
        resolved === "quiet" && "bg-transparent px-0 text-muted-foreground",
        className,
      )}
    >
      {dot ? <StatusDot tone={resolvedTone} onSolid={solid && !isStructural} /> : null}
      {children}
    </span>
  )
}

function StatusDot({
  tone,
  onSolid,
  label,
  ...props
}: Omit<React.HTMLAttributes<HTMLSpanElement>, "className" | "children"> & {
  tone: Tone
  onSolid?: boolean
  label?: string
}) {
  return (
    <span
      {...props}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      className={cn("size-2 shrink-0 rounded-full", onSolid ? "bg-current" : TONE_DOT[tone])}
    />
  )
}

function BadgeRemoveButton({
  label,
  className,
  ...props
}: Omit<IconButtonProps, "aria-label" | "children" | "size"> & {
  label: string
}) {
  return (
    <IconButton
      size="inline"
      aria-label={label}
      className={cn("-mr-1 rounded-full", className)}
      {...props}
    >
      <X aria-hidden />
    </IconButton>
  )
}

export { Badge, BadgeRemoveButton, StatusDot, VARIANT_TONE }
